// material-ui
import { alpha } from '@mui/material/styles';

// project import
import getColors from "../utils/getColors";
import getShadow from '../utils/getShadow';

// ==============================|| BUTTON - COLORS ||============================== //

function getColorStyle({ variant, color, theme }) {
	const colors = getColors(theme, color);
	
	const { lighter, main, dark, contrastText } = colors;
	
	const buttonShadow = `${color}Button`;
	const shadows = getShadow(theme, buttonShadow);
	
	const commonShadow = {
		'&::after': {
			boxShadow: `0 0 5px 5px ${alpha(main, 0.9)}`
		},
		'&:active::after': {
			boxShadow: `0 0 0 0 ${alpha(main, 0.9)}`
		},
		'&:focus-visible': {
			outline: `2px solid ${dark}`,
			outlineOffset: 2
		}
	}
	
	switch (variant) {
		case 'contained':
			return {
				'&:hover': {
					backgroundColor: dark,
					color: contrastText,
					boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset',
					//box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.20) inset;
				},
				// "&.Mui-disabled": {
				// 	backgroundColor: "lightblue", // Χρώμα όταν είναι disabled
				// 	color: "white", // Χρώμα κειμένου
				// },
				color: contrastText,
				background: main,
				'& .Mui-disabled': {
					color: 'red',
				},
				...commonShadow
			}
		case 'shadow':
			return {
				color: contrastText,
				backgroundColor: main,
				boxShadow: shadows,
				'&:hover': {
					boxShadow: 'none',
					backgroundColor: dark
				},
				...commonShadow
			}
		case 'outlined':
			return {
				color: main,
				backgroundColor: '#fff',
				border: '1.5px solid',
				borderColor: main,
				'&:hover': {
					color: dark,
					backgroundColor: '#fff',
					border: '1.5px solid',
					borderColor: dark
				},
				...commonShadow
			}
		case 'dashed':
			return {
				color: main,
				borderColor: main,
				backgroundColor: lighter,
				'&:hover': {
					color: dark,
					borderColor: dark
				},
				...commonShadow
			};
		case 'text':
		default:
			return {
				color: dark,
				'&:hover': {
					color: dark,
					backgroundColor: lighter,
				},
			}
	}
}

// ==============================|| OVERRIDES - BUTTON ||============================== //

export default function Button(theme) {
	const primaryDashed = getColorStyle({ variant: 'dashed', color: 'primary', theme });
	const primaryShadow = getColorStyle({ variant: 'shadow', color: 'primary', theme });

	
	const iconStyle = {
		'&>*:nth-of-type(1)': {
			fontSize: 'inherit'
		},
		// '&.MuiButton-startIcon.Mui-disabled, &.Mui-disabled > *': {
		// 	color: '#9CBCFF !important' // Ανενεργό icon
		// }
	}
	
	return {
		MuiButton: {
			defaultProps: {
				disableElevation: true,
				disableRipple: true
			},
			styleOverrides: {
				root: {
					fontWeight: 400,
					fontSize: '14px',
					'&::after': {
						content: '""',
						display: 'block',
						position: 'absolute',
						left: 0,
						top: 0,
						width: '100%',
						height: '100%',
						opacity: 0,
						transition: 'all 0.5s'
					},
					'&:active::after': {
						position: 'absolute',
						left: 0,
						top: 0,
						opacity: 1,
						transition: '0s'
					}
				},
				contained: {
					// ...disabledStyle
					'&.Mui-disabled': {
						color:theme.palette.highlight[300], //'#9CBCFF'
						backgroundColor:'#647DFF',
					}
				},
				outlined: {
					'&.Mui-disabled': {
						border: '1px solid',
						opacity: 0.6,
						color:theme.palette.highlight[300], //'#9CBCFF'
						background: '#FFF'
					}
				},
				text: {
					boxShadow: 'none',
					'&:hover': {
						boxShadow: 'none'
					}
				},
				endIcon: {
					...iconStyle
				},
				startIcon: {
					...iconStyle
				},
				dashed: {
					border: '1px dashed',
					...primaryDashed,
					'&.MuiButton-dashedPrimary': getColorStyle({ variant: 'dashed', color: 'primary', theme }),
					'&.MuiButton-dashedSecondary': getColorStyle({ variant: 'dashed', color: 'secondary', theme }),
					'&.MuiButton-dashedError': getColorStyle({ variant: 'dashed', color: 'error', theme }),
					'&.MuiButton-dashedSuccess': getColorStyle({ variant: 'dashed', color: 'success', theme }),
					'&.MuiButton-dashedInfo': getColorStyle({ variant: 'dashed', color: 'info', theme }),
					'&.MuiButton-dashedWarning': getColorStyle({ variant: 'dashed', color: 'warning', theme }),
					'&.Mui-disabled': {
						color: `${theme.palette.grey[300]} !important`,
						borderColor: `${theme.palette.grey[400]} !important`,
						backgroundColor: `${theme.palette.grey[200]} !important`
					}
				},
				shadow: {
					...primaryShadow,
					'&.MuiButton-shadowPrimary': getColorStyle({ variant: 'shadow', color: 'primary', theme }),
					'&.MuiButton-shadowSecondary': getColorStyle({ variant: 'shadow', color: 'secondary', theme }),
					'&.MuiButton-shadowError': getColorStyle({ variant: 'shadow', color: 'error', theme }),
					'&.MuiButton-shadowSuccess': getColorStyle({ variant: 'shadow', color: 'success', theme }),
					'&.MuiButton-shadowInfo': getColorStyle({ variant: 'shadow', color: 'info', theme }),
					'&.MuiButton-shadowWarning': getColorStyle({ variant: 'shadow', color: 'warning', theme }),
					'&.Mui-disabled': {
						color: `${theme.palette.grey[300]} !important`,
						borderColor: `${theme.palette.grey[400]} !important`,
						backgroundColor: `${theme.palette.grey[200]} !important`
					}
				},
				containedPrimary: getColorStyle({ variant: 'contained', color: theme.palette.secondary[100], theme }),
				containedSecondary: getColorStyle({ variant: 'contained', color: 'secondary', theme }),
				containedError: getColorStyle({ variant: 'contained', color: 'error', theme }),
				containedSuccess: getColorStyle({ variant: 'contained', color: 'success', theme }),
				containedInfo: getColorStyle({ variant: 'contained', color: 'info', theme }),
				containedWarning: getColorStyle({ variant: 'contained', color: 'warning', theme }),
				outlinedPrimary: getColorStyle({ variant: 'outlined', color: 'primary', theme }),
				outlinedBlue: getColorStyle({ variant: 'outlined', color: 'blue', theme }),
				outlinedSecondary: getColorStyle({ variant: 'outlined', color: 'secondary', theme }),
				outlinedError: getColorStyle({ variant: 'outlined', color: 'error', theme }),
				outlinedSuccess: getColorStyle({ variant: 'outlined', color: 'success', theme }),
				outlinedInfo: getColorStyle({ variant: 'outlined', color: 'info', theme }),
				outlinedWarning: getColorStyle({ variant: 'outlined', color: 'warning', theme }),
				textPrimary: getColorStyle({ variant: 'text', color: 'primary', theme }),
				textSecondary: getColorStyle({ variant: 'text', color: 'secondary', theme }),
				textError: getColorStyle({ variant: 'text', color: 'error', theme }),
				textSuccess: getColorStyle({ variant: 'text', color: 'success', theme }),
				textInfo: getColorStyle({ variant: 'text', color: 'info', theme }),
				textWarning: getColorStyle({ variant: 'text', color: 'warning', theme }),
				sizeLarge: {
					padding: '16px 48px',
					borderRadius: '12px',
					lineHeight: '1.125rem',
					fontSize: '0.875rem',
					fontWeight: 500
				},
				sizeMedium: {
					padding: '16px 48px',
					borderRadius: '12px',
					lineHeight: '1.125rem',
					fontSize: '0.875rem',
					fontWeight: 500
				},
				sizeSmall: {
					padding: '8px 24px 8px 16px',
					borderRadius: '8px',
					lineHeight: '1.25rem',
					fontSize: '0.875rem',
					fontWeight: 500
				},
				sizeExtraSmall: {
					lineHeight: '1.125rem',
					fontSize: '0.875rem',
					fontWeight: 500
				}
			}
		}
	}
}
